import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSearchParam} from "react-use";
import {useQuery} from "@apollo/client";
import dayjs from "dayjs";

import {Button, DropdownButton, Option, Text} from "../../components/input";
import {
	GET_COMPANY_SHARE_EVENTS,
	GET_USER_EVENTS,
	GET_USER_SHARE_EVENTS,
	SCHEDULE_QUEUE,
	SCHEDULE_SHARE_EVENT,
	ShareEvent,
	loadShareEvent,
	useMyUser,
} from "../../data";
import {defaultLoadingProps as loadingProps, usePaginatedQuery} from "../../paginated-query";
import {CollectionContext} from "./index";
import {usePageInput, usePageScroll} from "../../layout";
import {ShareItem} from "./share-item";
import {ToggleGroup} from "../../components/toggle";
import {useDebounce} from "../../debounce";
import {useMutationToast, useToast} from "../../toast";

import styles from "./collection.module.scss";

const empty = (
	<div className={styles.empty}>
		<img src="/svgs/collection_blank.svg" alt="No Posts" />
	</div>
);
const renderItem = (shareEvent: ShareEvent) => <ShareItem key={shareEvent.id} shareEvent={shareEvent} />;

const options: Option<boolean>[] = [
	{value: false, label: "Company"},
	{value: true, label: "Personal"},
];

export const Posts = (): ReactElement => {
	const [search, setSearch] = useState("");

	const debouncedSearch = useDebounce(search, 300);
	const {
		filter,
		tab: {personalShareEvents, setPersonalShareEvents},
	} = useContext(CollectionContext);
	const navigate = useNavigate();
	const user = useMyUser();
	const toast = useToast();

	const shareId = useSearchParam("queued_url_id");
	const queueId = useSearchParam("queue_id");
	const companyId = useSearchParam("company_user_id");

	const [scheduleSharesByQueueUrlId] = useMutationToast(SCHEDULE_SHARE_EVENT);
	const [scheduleSharesByQueueId] = useMutationToast(SCHEDULE_QUEUE);

	const createOptions = useMemo(
		() => [
			{label: "Company", onClick: () => navigate("new", {state: {type: "company"}})},
			{label: "Personal", onClick: () => navigate("new", {state: {type: "personal"}})},
		],
		[navigate]
	);

	useEffect(() => {
		const scheduleFunction = shareId ? scheduleSharesByQueueUrlId : queueId ? scheduleSharesByQueueId : null;
		if (scheduleFunction) {
			setPersonalShareEvents(!companyId);
			toast({
				color: "blue",
				text: queueId
					? "Scheduling all items from your Collection for sharing"
					: "Scheduling your post for sharing",
				timeout: 2,
			});
			scheduleFunction({
				variables: {id: queueId ?? shareId, ...(companyId && {companyId}), timezone: dayjs.tz.guess()},
				onCompleted: () => {
					toast({
						color: "green",
						text: queueId ? "Your posts have been scheduled!" : "Your post has been scheduled!",
					});
				},
			}).then(() => {
				navigate({search: ""});
			});
		}
	}, [
		navigate,
		toast,
		shareId,
		companyId,
		setPersonalShareEvents,
		queueId,
		scheduleSharesByQueueUrlId,
		scheduleSharesByQueueId,
	]);

	usePageInput(() => <Text value={search} onChange={setSearch} placeholder="Search" icon="search" />, [
		search,
	]);
	usePageInput(
		() =>
			user.role === "admin" ? (
				<DropdownButton value="Create Post" options={createOptions} />
			) : (
				<Button
					onClick={() => navigate("new", {state: {type: "personal"}})}
					value="Create Post"
					className={styles.dropdownButton}
				/>
			),
		[createOptions, navigate, user.role]
	);

	const variables = useMemo(
		() => ({
			limit: 20,
			sort: filter.sort?.toUpperCase(),
			status: filter.status?.map(s => s.toUpperCase()),
			groups: filter.groups.length ? filter.groups : undefined,
			owner: filter.owner,
			ids: filter.accounts.length && !personalShareEvents ? filter.accounts : undefined,
			search: debouncedSearch,
		}),
		[filter, debouncedSearch, personalShareEvents]
	);

	const SCHEDULED_SHARE_EVENTS_QUERY =
		!personalShareEvents && user.role === "admin" ? GET_COMPANY_SHARE_EVENTS : GET_USER_SHARE_EVENTS;

	const {handleScroll, render, data} = usePaginatedQuery<ShareEvent>(SCHEDULED_SHARE_EVENTS_QUERY, {
		loadingProps,
		inflateItem: loadShareEvent,
		variables,
		renderItem,
		itemsClassName: styles.collectionList,
		empty,
	});
	usePageScroll(handleScroll);

	const toBeSentPosts = useMemo(
		() =>
			data
				?.filter(share => !share.sharedAt && dayjs(share?.scheduledFor).isBefore())
				?.map(share => share.id) ?? [],
		[data]
	);
	const {stopPolling, startPolling} = useQuery(GET_USER_EVENTS, {
		variables: {ids: toBeSentPosts},
		skip: !toBeSentPosts?.length,
	});

	useEffect(() => {
		if (toBeSentPosts.length === 0) return stopPolling();
		startPolling(5000);
	}, [startPolling, stopPolling, toBeSentPosts]);

	return (
		<>
			{user.role === "admin" && (
				<div>
					<ToggleGroup options={options} onChange={setPersonalShareEvents} value={personalShareEvents} />
				</div>
			)}
			{render}
		</>
	);
};
